/**
 * Fuzzy + Boolean search engine for Orchist surfaces.
 *
 * Phase G.7.8 — JL: "FAB capabilities! ... Fuzzy AND b___ something I
 * forgot haha" → Fuzzy AND Boolean.
 *
 * Capabilities:
 *
 *   FUZZY (typo tolerance):
 *     - Terms ≥ 4 chars use word-level Levenshtein with tolerance 1–2
 *       (1 typo if 4–6 chars, 2 typos if ≥ 7 chars).
 *     - Terms < 4 chars require exact substring match (too short to
 *       fuzz without false positives).
 *     - Quoted phrases ("...") are always exact substring (no fuzz).
 *
 *   BOOLEAN (logic):
 *     - Whitespace between terms = AND (default)
 *     - Explicit `OR` (case-insensitive) groups adjacent terms into
 *       OR-clauses: `art OR matthew` matches if either appears
 *     - `-term` or `NOT term` excludes
 *     - `"quoted phrase"` is an exact substring match (no fuzz)
 *     - Operator precedence: NOT > AND > OR (standard)
 *
 * Examples:
 *     `founding`                → fuzzy match "founding" anywhere
 *     `fundng`                  → fuzzy match "founding" (1 typo)
 *     `art OR matthew`          → match if "art" OR "matthew" present
 *     `founding -acknowledged`  → "founding" but NOT "acknowledged"
 *     `"the founding orchist"`  → exact phrase
 *     `art 2026 -2025`          → "art" AND "2026" AND NOT "2025"
 *     `(art OR matthew) 2026`   → grouping with parens (deferred)
 *
 * @scale-marker stage-b — at ~100k Orchists, the per-row Levenshtein scan
 *   becomes expensive. Switch to Postgres pg_trgm (similarity()) for
 *   server-side fuzzy + tsvector for boolean. The grammar parsed here can
 *   compile to a Postgres ts_query directly. See sacred/scale-stewardship.md.
 */

// ---------------------------------------------------------------------------
// Levenshtein distance (small + dependency-free)
// ---------------------------------------------------------------------------

export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // Two-row DP for memory efficiency
  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,         // insert
        prev[j] + 1,              // delete
        prev[j - 1] + cost        // substitute
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

// ---------------------------------------------------------------------------
// Query parser
// ---------------------------------------------------------------------------

export type Term =
  | { kind: 'word'; text: string }       // fuzzy when length ≥ 4
  | { kind: 'phrase'; text: string };    // exact substring

export interface ParsedQuery {
  /** Each clause must match (logical AND). A clause is either a single term or an OR-group. */
  mustClauses: ({ kind: 'term'; term: Term } | { kind: 'or'; terms: Term[] })[];
  /** None of these terms may match (logical NOT). */
  mustNotTerms: Term[];
  /** True if the parsed query is empty (matches everything). */
  isEmpty: boolean;
}

const OR_TOKEN = /^OR$/i;
const NOT_TOKEN = /^NOT$/i;

export function parseQuery(raw: string): ParsedQuery {
  const empty: ParsedQuery = { mustClauses: [], mustNotTerms: [], isEmpty: true };
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return empty;

  // Tokenize, respecting quoted phrases.
  const tokens: Array<{ value: string; phrase: boolean; negated: boolean }> = [];
  let i = 0;
  while (i < trimmed.length) {
    const ch = trimmed[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    let negated = false;
    if (ch === '-' && i + 1 < trimmed.length && !/\s/.test(trimmed[i + 1])) {
      negated = true;
      i++;
    }
    if (trimmed[i] === '"') {
      const end = trimmed.indexOf('"', i + 1);
      if (end === -1) {
        // Unterminated quote — treat rest as phrase
        tokens.push({ value: trimmed.slice(i + 1), phrase: true, negated });
        break;
      }
      tokens.push({ value: trimmed.slice(i + 1, end), phrase: true, negated });
      i = end + 1;
    } else {
      let j = i;
      while (j < trimmed.length && !/\s/.test(trimmed[j]) && trimmed[j] !== '"') j++;
      const value = trimmed.slice(i, j);
      tokens.push({ value, phrase: false, negated });
      i = j;
    }
  }

  // Now apply NOT / OR semantics.
  const mustClauses: ParsedQuery['mustClauses'] = [];
  const mustNotTerms: Term[] = [];
  let pendingOrBuffer: Term[] | null = null;

  function flushOrBuffer() {
    if (pendingOrBuffer && pendingOrBuffer.length > 0) {
      if (pendingOrBuffer.length === 1) {
        mustClauses.push({ kind: 'term', term: pendingOrBuffer[0] });
      } else {
        mustClauses.push({ kind: 'or', terms: pendingOrBuffer });
      }
      pendingOrBuffer = null;
    }
  }

  let k = 0;
  while (k < tokens.length) {
    const t = tokens[k];

    // Standalone NOT keyword: negate the next non-keyword token
    if (!t.phrase && NOT_TOKEN.test(t.value)) {
      const nxt = tokens[k + 1];
      if (nxt) {
        mustNotTerms.push({
          kind: nxt.phrase ? 'phrase' : 'word',
          text: nxt.value.toLowerCase(),
        });
        k += 2;
        continue;
      }
      k += 1;
      continue;
    }

    // -term shorthand for NOT
    if (t.negated) {
      mustNotTerms.push({
        kind: t.phrase ? 'phrase' : 'word',
        text: t.value.toLowerCase(),
      });
      k += 1;
      continue;
    }

    // OR keyword: bridges previous and next term into an OR group.
    if (!t.phrase && OR_TOKEN.test(t.value)) {
      // If we don't yet have an OR buffer, take the last AND clause as
      // the first OR member.
      if (!pendingOrBuffer) {
        const last = mustClauses.pop();
        if (last) {
          if (last.kind === 'term') pendingOrBuffer = [last.term];
          else pendingOrBuffer = last.terms;
        } else {
          pendingOrBuffer = [];
        }
      }
      // Consume next token into the OR buffer
      const nxt = tokens[k + 1];
      if (nxt && !(NOT_TOKEN.test(nxt.value) && !nxt.phrase) && !nxt.negated) {
        pendingOrBuffer.push({
          kind: nxt.phrase ? 'phrase' : 'word',
          text: nxt.value.toLowerCase(),
        });
        k += 2;
        continue;
      }
      // Malformed (OR with no next term) — drop OR
      k += 1;
      continue;
    }

    // Regular AND term — flush any in-flight OR buffer first
    flushOrBuffer();
    mustClauses.push({
      kind: 'term',
      term: { kind: t.phrase ? 'phrase' : 'word', text: t.value.toLowerCase() },
    });
    k += 1;
  }

  flushOrBuffer();

  return {
    mustClauses,
    mustNotTerms,
    isEmpty: mustClauses.length === 0 && mustNotTerms.length === 0,
  };
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/** Returns true if any word in the haystack fuzz-matches the term. */
function termMatches(haystack: string, haystackWords: string[], term: Term): boolean {
  if (term.kind === 'phrase') {
    return haystack.includes(term.text);
  }
  // Exact substring first — fast path + handles compound words
  if (haystack.includes(term.text)) return true;

  // Fuzzy: word-level Levenshtein
  const t = term.text;
  if (t.length < 4) return false;
  const tolerance = t.length >= 7 ? 2 : 1;
  for (const w of haystackWords) {
    if (Math.abs(w.length - t.length) > tolerance) continue;
    if (levenshtein(w, t) <= tolerance) return true;
  }
  return false;
}

export function evaluateQuery(haystack: string, query: ParsedQuery): boolean {
  if (query.isEmpty) return true;
  const lc = haystack.toLowerCase();
  const words = lc.split(/[\s\-_·,;:."'()]+/).filter(Boolean);

  // All must-clauses must satisfy
  for (const clause of query.mustClauses) {
    if (clause.kind === 'term') {
      if (!termMatches(lc, words, clause.term)) return false;
    } else {
      // OR-group: at least one must match
      const anyMatch = clause.terms.some((t) => termMatches(lc, words, t));
      if (!anyMatch) return false;
    }
  }

  // No must-not-term may match
  for (const t of query.mustNotTerms) {
    if (termMatches(lc, words, t)) return false;
  }

  return true;
}

// ---------------------------------------------------------------------------
// Convenience — one-shot match
// ---------------------------------------------------------------------------

export function matches(haystack: string, query: string): boolean {
  return evaluateQuery(haystack, parseQuery(query));
}
